import React, { useState, useEffect } from 'react';
import { formatDateShort, calculateDaysUntilExpiration } from '../../utils/screenerDataHelpers';
import '../../styles/MobileScreener.css';

/**
 * Global simulation controls for expiration-specific settings
 * Allows users to set Days to Exit and Underlying Gain for all contracts
 * Updated to match Table View features: hot buttons, date display, collapsible panel
 */
const GlobalSimulationControls = ({
  activeExpiration,
  expirationSettings,
  setExpirationSettings,
  resultsGrouped,
  onRefreshAll,
  originalSettings
}) => {
  const [days, setDays] = useState(45);
  const [gain, setGain] = useState(10);
  const [maxDays, setMaxDays] = useState(365);
  const [refreshing, setRefreshing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Default values for reset
  const DEFAULT_DAYS = 45;
  const DEFAULT_GAIN = 10;

  // Hot button values - full set on desktop, simplified on mobile
  const hotButtons = isMobile
    ? [-20, 20, 1000]
    : [-50, -35, -20, -10, 10, 20, 35, 50, 100, 200, 1000];

  // Load settings when expiration changes
  useEffect(() => {
    const settings = expirationSettings[activeExpiration] || expirationSettings['all'] || { days: DEFAULT_DAYS, gain: DEFAULT_GAIN };
    setDays(settings.days);
    setGain(settings.gain);

    // Calculate max days based on active expiration
    if (activeExpiration === 'all') {
      // Find the longest expiration across all contracts
      let max = 90;
      Object.values(resultsGrouped).forEach(contracts => {
        contracts.forEach(contract => {
          const daysUntilExp = calculateDaysUntilExpiration(contract.Expiration || contract.expiration);
          max = Math.max(max, daysUntilExp);
        });
      });
      setMaxDays(max);
    } else {
      // Use specific expiration date
      const daysUntilExp = calculateDaysUntilExpiration(activeExpiration);
      setMaxDays(daysUntilExp);
    }
  }, [activeExpiration, expirationSettings, resultsGrouped]);

  // Track changes from defaults
  useEffect(() => {
    const currentSettings = expirationSettings[activeExpiration] || expirationSettings['all'] || { days: DEFAULT_DAYS, gain: DEFAULT_GAIN };
    const changed = days !== currentSettings.days || gain !== currentSettings.gain;
    setHasChanges(changed);
  }, [days, gain, activeExpiration, expirationSettings]);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Date calculation functions (matching Table View)
  const getTargetDate = (daysFromNow) => {
    const today = new Date();
    return new Date(today.getTime() + (daysFromNow * 24 * 60 * 60 * 1000));
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateWithDays = (date, numDays) => {
    return `${formatDate(date)} (${numDays} d.)`;
  };

  const getFormattedTargetDate = () => {
    const targetDate = getTargetDate(days);
    return formatDateWithDays(targetDate, days);
  };

  const getFormattedMaxDate = () => {
    const maxDate = getTargetDate(maxDays);
    return formatDateWithDays(maxDate, maxDays);
  };

  // Format gain label
  const formatGainLabel = (value) => {
    if (value === 0) return '0%';
    return value > 0 ? `+${value}%` : `${value}%`;
  };

  // Save settings when changed
  const handleDaysChange = (e) => {
    const value = parseInt(e.target.value);
    setDays(value);
    setExpirationSettings(prev => ({
      ...prev,
      [activeExpiration]: { days: value, gain }
    }));
  };

  const handleGainChange = (e) => {
    const value = parseInt(e.target.value);
    setGain(value);
    setExpirationSettings(prev => ({
      ...prev,
      [activeExpiration]: { days, gain: value }
    }));
  };

  // Hot button click handler
  const handleHotButton = (value) => {
    setGain(value);
    setExpirationSettings(prev => ({
      ...prev,
      [activeExpiration]: { days, gain: value }
    }));
  };

  // Reset to original queue values (or defaults if not available)
  const handleReset = () => {
    const resetDays = originalSettings?.days || DEFAULT_DAYS;
    const resetGain = originalSettings?.gain || DEFAULT_GAIN;

    setDays(resetDays);
    setGain(resetGain);
    setExpirationSettings(prev => ({
      ...prev,
      [activeExpiration]: { days: resetDays, gain: resetGain }
    }));
    setHasChanges(false);
  };

  // Count contracts for display
  const contractCount = Object.values(resultsGrouped).reduce((sum, contracts) => {
    if (activeExpiration === 'all') {
      return sum + contracts.length;
    }
    return sum + contracts.filter(c =>
      (c.Expiration || c.expiration) === activeExpiration
    ).length;
  }, 0);

  // Handle refresh button click
  const handleRefresh = async () => {
    if (onRefreshAll && !refreshing) {
      setRefreshing(true);
      try {
        await onRefreshAll();
      } finally {
        setRefreshing(false);
      }
    }
  };

  // Toggle panel expansion
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="global-controls-panel">
      {/* Collapsible Header */}
      <div
        className="global-controls-header"
        onClick={toggleExpanded}
      >
        <div className="global-header-left">
          <h3 className="global-header-title">Simulation Settings</h3>
          <span className="global-header-info">
            {activeExpiration === 'all' ? 'All' : formatDateShort(activeExpiration)}
            {' '}({contractCount} contracts)
          </span>
        </div>
        <div className="global-header-right">
          {!isExpanded && (
            <span className="global-header-summary">
              {days}d, {formatGainLabel(gain)}
            </span>
          )}
          <span className={`global-chevron ${isExpanded ? 'expanded' : ''}`}>
            ›
          </span>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="global-controls-content">
          {/* Days to Exit Slider */}
          <div className="global-control-section">
            <div className="global-control-header">
              <span className="global-control-label">Days to Exit</span>
              <span className="global-control-date">{getFormattedTargetDate()}</span>
            </div>
            <div className="global-slider-container">
              <input
                type="range"
                className="global-control-slider"
                min="1"
                max={maxDays}
                value={days}
                onChange={handleDaysChange}
                style={{
                  background: `linear-gradient(to right, #218aff 0%, #218aff ${
                    (days / maxDays) * 100
                  }%, #374151 ${(days / maxDays) * 100}%, #374151 100%)`
                }}
              />
              <div className="global-slider-labels">
                <span>1 d.</span>
                <span>{Math.floor(maxDays / 2)} d.</span>
                <span>{getFormattedMaxDate()}</span>
              </div>
            </div>
          </div>

          {/* Underlying Gain Slider */}
          <div className="global-control-section">
            <div className="global-control-header">
              <span className="global-control-label">Underlying Gain</span>
              <span className="global-control-value-display">{formatGainLabel(gain)}</span>
            </div>
            <div className="global-slider-container">
              <input
                type="range"
                className="global-control-slider"
                min="-90"
                max="200"
                value={Math.min(gain, 200)}
                onChange={handleGainChange}
                style={{
                  background: `linear-gradient(to right, #218aff 0%, #218aff ${
                    gain > 200
                      ? 100
                      : ((Math.min(gain, 200) + 90) / 290) * 100
                  }%, #374151 ${
                    gain > 200
                      ? 100
                      : ((Math.min(gain, 200) + 90) / 290) * 100
                  }%, #374151 100%)`
                }}
              />
              <div className="global-slider-labels">
                <span>-90%</span>
                <span>+55%</span>
                <span>+200%</span>
              </div>
            </div>

            {/* Hot Buttons */}
            <div className="global-hot-buttons">
              {hotButtons.map((value) => (
                <button
                  key={value}
                  onClick={() => handleHotButton(value)}
                  className={`global-hot-btn ${gain === value ? 'active' : ''}`}
                >
                  {formatGainLabel(value)}
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="global-action-buttons">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="global-refresh-btn"
              title="Refresh all contracts with current settings"
            >
              {refreshing ? 'Updating...' : 'Refresh'}
            </button>
            <button
              onClick={handleReset}
              className="global-reset-btn"
              title="Reset to original queue values"
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSimulationControls;
